<template>
  <div class="threeViewer flex-1" ref="viewer" :class="{ 'bg-white': bgWhite }"></div>
</template>

<script>
import * as THREE from 'three';
import TWEEN from 'tween.js';
import { Line2 } from 'three/examples/jsm/lines/Line2';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils';

import ShaderPresets from '@/lib/ShaderPresets';

import { TGSLoader } from '../../modules/TGSLoader';

export default {
  name: 'ModelViewer',
  async mounted() {
    window.addEventListener('resize', this.onWindowResize);
    if (this.objUrl) {
      this.renderModel(this.objUrl);
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize);
    this.stopTween();
    if (this.useEnvMap && this.scene.background) {
      this.scene.background.dispose();
      this.scene.background = null;
    }
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
      this.renderer = null;
    }
  },
  props: {
    objUrl: String,
    materialColor: Number,
    measurement: {
      type: [Object, Array],
      default: () => ({}),
    },
    bgWhite: Boolean,
    fov: {
      type: Number,
      default: 45,
    },
    zoomFactor: {
      type: Number,
      default: 1.5,
    },
    zoomHeight: {
      // zoomToFit with height, unit: mm  TODO: implement in comparison view
      type: Number,
      default: -1,
    },
    disableEnterAnimation: {
      type: Boolean,
      default: false,
    },
    disableControl: {
      type: Boolean,
      default: false,
    },
    controlDomElement: {
      type: HTMLElement,
      default: null,
    },
    showFeet: {
      type: Array,
      default: () => [true, true],
    },
    cameraPositionX: {
      type: Number,
      default: 0,
    },
    cameraPositionY: {
      type: Number,
      default: 0,
    },
    maxPolarAngle: {
      type: Number,
      default: (Math.PI * 4) / 6,
    },
    minPolarAngle: {
      type: Number,
      default: (Math.PI * 1) / 6,
    },
    shaderType: {
      type: String,
      default: 'default',
    },
    customShader: Object,
    useEnvMap: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    running: false,
    stopAnimating: false,
    standbyAfter: 2000,
    lastMove: Date.now(),
    animateHandle: -1,
    modelLoaded: false,
    currLines: [],
  }),
  methods: {
    async renderModel(objUrl) {
      // setup scene
      this.modelLoaded = false;
      this.container = this.$refs.viewer;
      this.scene = new THREE.Scene();
      this.renderer = new THREE.WebGLRenderer({
        antialias: true,
        preserveDrawingBuffer: true,
        alpha: true,
      });
      this.renderer.outputEncoding = THREE.sRGBEncoding;
      this.container.appendChild(this.renderer.domElement);
      this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
      this.renderer.setPixelRatio(this.container.clientWidth / this.container.clientHeight);
      this.renderer.domElement.style.width = '100%';
      this.renderer.domElement.style.height = '100%';

      this.renderer.domElement.addEventListener('touchmove', this.stopTween, false);

      const textureLoader = new THREE.TextureLoader();
      const canvas = document.createElement('canvas');
      canvas.width = 128;
      canvas.height = 128;
      const context = canvas.getContext('2d');
      const gradient = context.createRadialGradient(64, 64, 0, 64, 64, 64); // 圓心 (64, 64)，半徑 64
      gradient.addColorStop(0, 'rgba(255, 255, 255, 1)'); // 半徑 0，完全不透明
      gradient.addColorStop(0.2, 'rgba(255, 255, 255, 1)'); // 半徑 50，完全不透明
      gradient.addColorStop(0.2, 'rgba(255, 255, 255, 0.2)'); // 半徑 50，完全不透明
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)'); // 半徑 64，完全透明
      context.fillStyle = gradient;
      context.beginPath();
      context.arc(64, 64, 64, 0, 2 * Math.PI); // 圓心 (64, 64)，半徑 64
      context.fill();
      this.pointTextureGlow = new THREE.CanvasTexture(canvas);
      this.pointTexture = await textureLoader.loadAsync('/sprites/disc.png');
      if (this.useEnvMap) {
        this.scene.background = await textureLoader.loadAsync('/bg-HDR.jpg');
        this.scene.background.encoding = THREE.sRGBEncoding;
        this.scene.background.mapping = THREE.EquirectangularReflectionMapping;
      }

      this.matLine = new LineMaterial({
        color: new THREE.Color(0x8146ff).convertSRGBToLinear(),
        vertexColors: false,
        linewidth: 3,
        dashed: false,
      });
      this.matPlane = new THREE.MeshBasicMaterial({
        color: new THREE.Color(0x6eaeff).convertSRGBToLinear(),
        side: THREE.DoubleSide,
        transparent: true,
        depthTest: true,
        opacity: 0.4,
      });
      this.matRefLine = new LineMaterial({
        color: new THREE.Color(0x22262b).convertSRGBToLinear(),
        vertexColors: false,
        linewidth: 3,
        dashed: false,
      });

      // setup lights
      const lightGroup = new THREE.Group();
      const ambientLight = new THREE.AmbientLight(0x808080, 0.6);
      const mainDirectLight = new THREE.DirectionalLight(0xffffff, 0.45);
      mainDirectLight.position.set(-2.175, 3.744, 3.223);
      const frontPointLight = new THREE.PointLight(0xffffff, 0.45);
      const leftPointLight = new THREE.PointLight(0xfffef0, 0.13);
      const rightPointLight = new THREE.PointLight(0x82aade, 0.4);
      frontPointLight.position.set(-0.585, 2.126, 1.065);
      leftPointLight.position.set(-0.535, 1.221, -0.753);
      rightPointLight.position.set(0.899, 1.57, -0.681);

      lightGroup.add(ambientLight);
      lightGroup.add(mainDirectLight);
      lightGroup.add(frontPointLight);
      lightGroup.add(leftPointLight);
      lightGroup.add(rightPointLight);
      lightGroup.scale.set(1000, 1000, 1000);
      this.scene.add(lightGroup);

      // const { lights } = this.actualPreset;
      // const ambientLight = new THREE.AmbientLight(...lights.ambient);
      // const light = new THREE.DirectionalLight(...lights.light);
      // const light2 = new THREE.DirectionalLight(...lights.light2);

      // light.position.set(0, 0.3, 0.7).normalize();
      // light2.position.set(0, 0.2, -0.7).normalize();

      // this.scene.add(ambientLight);
      // this.scene.add(light);
      // this.scene.add(light2);

      // setup camera
      this.camera = new THREE.PerspectiveCamera(
        this.fov,
        this.container.clientWidth / this.container.clientHeight,
        1,
        10000
      );
      this.controls = new OrbitControls(
        this.camera,
        this.controlDomElement || this.renderer.domElement
      );
      this.controls.target.set(0, 0, 0); // view direction perpendicular to XY-plane
      this.controls.enableRotate = !this.disableControl;
      this.controls.enablePan = false;
      this.controls.enableZoom = false;
      this.controls.maxPolarAngle = this.maxPolarAngle;
      this.controls.minPolarAngle = this.minPolarAngle;
      this.controls.autoRotate = false;
      this.controls.minDistance = 500;
      this.updateResolution();

      this.controls.addEventListener('change', () => {
        lightGroup.rotation.copy(this.camera.rotation);
      });
      this.controls.addEventListener('change', this.requestRender, false);

      // load model
      if (objUrl) this.loadModel(objUrl);
    },
    loadModel(objUrl) {
      if (this.mesh) {
        this.remove(this.mesh);
        this.mesh = null;
      }
      if (this.pointCloud) {
        this.remove(this.pointCloud);
        this.pointCloud = null;
      }

      const tgsLoader = new TGSLoader();
      tgsLoader.load(objUrl, mesh => {
        this.assignUVs(mesh.geometry);
        this.geometry = mesh.geometry;
        const visible = Array(this.geometry.attributes.position.count).fill(1);
        this.geometry.setAttribute('visible', new THREE.Float32BufferAttribute(visible, 1));

        this.mesh = mesh;
        this.mesh.position.z = 0;
        this.resetCamera();

        if (this.isPointShader) {
          const material = new THREE.PointsMaterial({
            color: this.actualMaterialColor,
            size: 7,
            onBeforeCompile: shader => {
              shader.vertexShader = shader.vertexShader // eslint-disable-line no-param-reassign
                .replace(
                  `uniform float size;`,
                  `attribute float visible;
                   varying float vVisible;
                   uniform float size;
                  `
                )
                .replace(
                  `gl_PointSize = size;`,
                  `vVisible = visible;
                   gl_PointSize = size;
                  `
                );
              shader.fragmentShader = shader.fragmentShader // eslint-disable-line no-param-reassign
                .replace(
                  `uniform vec3 diffuse;`,
                  `varying float vVisible;
                   uniform vec3 diffuse;
                  `
                )
                .replace(
                  `#include <clipping_planes_fragment>`,
                  `if (vVisible <= 0.0) {
                     discard;
                   }
                   #include <clipping_planes_fragment>
                  `
                );
            },
          });
          this.pointCloud = new THREE.Points(this.geometry, material);
          this.scene.add(this.pointCloud);
          this.modelLoaded = true;
          this.enterWithRotation();
        } else {
          this.setMaterial();
        }
        this.requestRender();
      });
    },
    enterWithRotation() {
      if (this.disableControl || this.disableEnterAnimation) return;

      const rotation = { angle: -90 };
      const tween = new TWEEN.Tween(rotation)
        .to({ angle: 0 }, 3000)
        .onUpdate(() => {
          this.controls.reset();
          this.controls.rotateLeft(rotation.angle * (Math.PI / 180));
          this.controls.update();
        })
        .easing(TWEEN.Easing.Quartic.Out);
      tween.start();
      this.tween = tween;
    },
    stopTween() {
      if (this.tween) {
        this.tween.stop();
        this.tween = null;
      }
    },
    setMaterial() {
      const { material } = this.actualPreset;
      const materialPromises = new Promise(resolve => {
        const options = {
          transparent: true,
          depthTest: true,
          wireframe: false,
          polygonOffset: true,
          polygonOffsetFactor: 1,
          polygonOffsetUnits: 10,
          ...material,
          onBeforeCompile: shader => {
            shader.vertexShader = shader.vertexShader // eslint-disable-line no-param-reassign
              .replace(
                `#define PHONG`,
                `#define PHONG
                  attribute float visible;
                  varying float vVisible;
                `
              )
              .replace(
                `#include <uv_vertex>`,
                `vVisible = visible;
                 #include <uv_vertex>
                `
              );
            shader.fragmentShader = shader.fragmentShader // eslint-disable-line no-param-reassign
              .replace(
                `#define PHONG`,
                `#define PHONG
                 varying float vVisible;
                `
              )
              .replace(
                `#include <clipping_planes_fragment>`,
                `if (vVisible <= 0.0) {
                   discard;
                 }
                 #include <clipping_planes_fragment>
                `
              );
          },
        };

        this.material = new THREE.MeshPhongMaterial(options);

        if (!this.actualMaterialColor) {
          this.crateTexture = new THREE.TextureLoader().load(
            'linen_512_contrast2_32colors.png',
            () => {
              this.crateTexture.wrapS = THREE.RepeatWrapping;
              this.crateTexture.wrapT = THREE.RepeatWrapping;
              this.crateTexture.repeat.set(0.005, 0.005);

              this.material.map = this.crateTexture;
              resolve();
            }
          );
        } else {
          this.material.color = new THREE.Color(this.actualMaterialColor);
          resolve();
        }
      });

      materialPromises.then(() => {
        this.mesh.material = this.material;
        this.scene.add(this.mesh);
        this.modelLoaded = true;
        this.enterWithRotation();
      });
    },
    assignUVs(geometry) {
      const { count } = geometry.attributes.position;
      const uvs = new Float32Array(count * 2);
      const v = new THREE.Vector3();
      for (let i = 0; i < count; i += 1) {
        v.fromBufferAttribute(geometry.attributes.position, i);
        uvs[i * 2] = v.x;
        uvs[i * 2 + 1] = v.y;
      }
      geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
    },
    resetCamera() {
      this.controls.maxPolarAngle = this.maxPolarAngle;
      this.controls.minPolarAngle = this.minPolarAngle;
      this.zoomToFit(this.mesh, this.zoomFactor);

      // camera reset data
      this.controls.target0 = this.controls.target.clone();
      this.controls.position0 = this.controls.object.position.clone();
      this.controls.rotateLeft(this.cameraPositionX);
      this.controls.rotateUp(this.cameraPositionY);
    },
    zoomToFit(object, offset = 1) {
      this.stopTween();
      const bbox = object instanceof THREE.Box3 ? object : new THREE.Box3().setFromObject(object);
      const center = bbox.getCenter(new THREE.Vector3());
      const size = bbox.getSize(new THREE.Vector3());
      const fov = this.camera.fov * (Math.PI / 180);
      const radius = this.zoomHeight > 0 ? this.zoomHeight : Math.max(size.x, size.y) / 2;
      const cameraZ = center.z + Math.abs(radius / Math.tan(fov / 2)) * offset;
      this.camera.position.set(center.x, center.y, cameraZ);

      if (cameraZ > this.camera.far) {
        this.camera.far = cameraZ + 1000;
        this.camera.near = Math.abs(this.camera.far) / 100;
        this.camera.updateProjectionMatrix();
      }

      if (this.controls) {
        this.controls.target.set(center.x, center.y, center.z);
        this.controls.update();
      }
    },
    showHideFoot(showLeft, showRight) {
      for (let i = 0; i < this.geometry.attributes.position.count; i += 1) {
        if (this.geometry.attributes.position.array[i * 3] > 0) {
          this.geometry.attributes.visible.array[i] = showLeft ? 1 : 0;
        } else {
          this.geometry.attributes.visible.array[i] = showRight ? 1 : 0;
        }
      }
      this.geometry.attributes.visible.needsUpdate = true; // eslint-disable-line no-param-reassign
      this.requestRender();
    },
    requestRender() {
      this.lastMove = Date.now();
      if (!this.running) {
        requestAnimationFrame(this.animate);
      }
    },
    animate(time) {
      if (this.lastMove + this.standbyAfter < Date.now()) {
        this.running = false;
      } else {
        this.running = true;
      }

      if (this.running && this.animateHandle === -1) {
        this.animateHandle = setTimeout(() => {
          this.animateHandle = -1;
          requestAnimationFrame(this.animate);
        }, 33.333);
      }

      this.render();
      TWEEN.update(time);
    },
    remove(object) {
      object.traverse(child => {
        if (child.geometry && child.geometry.dispose) child.geometry.dispose();
      });
      this.scene.remove(object);
    },
    clearMeasurements() {
      if (!Array.isArray(this.currLines) || !this.currLines.length) return;

      this.currLines.forEach(l => {
        this.scene.remove(l);
      });
      this.currLines = [];
      this.animate();
    },
    drawMeasurement(measurement) {
      if (!this.modelLoaded || !measurement) return;

      const { key, rightMes, type, algVer } = measurement;
      const positions = this.measurementToPositions(measurement);
      if (!positions || positions.length < 2) return;

      const rPositions = rightMes ? this.measurementToPositions(rightMes) : [];

      // reset
      this.mesh.material.transparent = true;
      if (this.mesh.material.originOpacity) {
        this.mesh.material.opacity = this.mesh.material.originOpacity;
        delete this.mesh.material.originOpacity;
      }
      if (this.needResetCamera) {
        this.needResetCamera = false;
        this.camera.fov = this.fov;
        this.camera.near = 1;
        this.camera.far = 10000;
        this.camera.updateProjectionMatrix();
        this.resetCamera();
      }
      this.controls.enableRotate = !this.disableControl;

      if (key === 'C19 Breast Triangle') {
        // 客戶要求線要在平面之前
        for (let i = 2; i < positions.length; i += 3) {
          positions[i] += 20;
        }
      }
      if (
        key === 'C19 Crotch to Ankle Degree' ||
        key === 'C19 Right Neck to Ankle Degree' ||
        key === 'C19 Left Neck to Ankle Degree'
      ) {
        const plane = this.drawPlane();
        const z = positions[5];
        plane.position.z = z;
        this.currLines.push(plane);
        this.scene.add(plane);
        this.mesh.material.transparent = false;
        this.resetCamera();
        this.controls.rotateLeft(90 * (Math.PI / 180));
        this.needResetCamera = true;
      }
      if (key === 'C19 Spine Degree') {
        const plane = this.drawPlane(true);
        const x = positions[6];
        plane.position.x = x;
        this.currLines.push(plane);
        this.scene.add(plane);
        this.mesh.material.transparent = false;
        this.resetCamera();
        this.animate();
        return;
      }
      if ((key === 'Left Breast Volume' || key === 'Right Breast Volume') && algVer < 1080) {
        if (rPositions.length > 0) {
          positions.push(...rPositions);
        }
        const volume = this.drawVolume(positions);
        volume.position.z = 2;
        this.currLines.push(volume);
        this.scene.add(volume);

        this.mesh.material.transparent = false;
        this.needResetCamera = true;
        this.zoomToFit(volume, 3.5);

        this.animate();
        return;
      }
      if (type === 'volume' && algVer >= 1080) {
        if (rPositions.length > 0) {
          positions.push(...rPositions);
        }
        const { glow } = measurement;
        const cloud = this.drawPointCloud(positions, 0x4300d9, 6, glow);
        this.currLines.push(cloud);
        this.scene.add(cloud);

        this.mesh.material.transparent = false;
        this.needResetCamera = true;
        this.zoomToFit(cloud, 3.5);
      } else {
        this.addLine(positions, this.matLine, 1.2);
        this.createReferenceLines(measurement);

        if (rPositions.length > 0) {
          this.addLine(rPositions, this.matLine, 1.2);
          this.createReferenceLines(rightMes);
        }
      }

      // specail ref points, for breast
      const { ref_points: refPoints } = measurement;
      if (refPoints && refPoints.length === 4) {
        const plane = this.drawBreastTriangle(refPoints.slice(0, 3).flat());
        this.currLines.push(plane);
        this.scene.add(plane);

        this.needResetCamera = true;
        this.camera.fov = 5;
        this.camera.updateProjectionMatrix();
        this.zoomToFit(plane, 3.5);
        this.controls.enableRotate = false;
      }
      // specail annotate_line
      const { annotate_line: annotateLine } = measurement;
      if (Array.isArray(annotateLine)) {
        this.addLine(this.linePointsToPositions(annotateLine, false), this.matRefLine, 1.2);
      }

      // custom draw settings
      const { view, zoom, fov, opacity } = measurement;
      if (Number.isFinite(fov)) {
        this.needResetCamera = true;
        this.camera.fov = fov;
        this.camera.updateProjectionMatrix();
        if (fov === 5) this.controls.enableRotate = false;
      }
      if (Number.isFinite(zoom)) {
        this.needResetCamera = true;
        const bbox = new THREE.Box3();
        this.currLines.forEach(l => {
          bbox.expandByObject(l);
        });
        this.zoomToFit(bbox, zoom);
      }
      if (view) {
        this.needResetCamera = true;
        if (view === 'left') this.controls.rotateLeft(-90 * (Math.PI / 180));
        if (view === 'right') this.controls.rotateLeft(90 * (Math.PI / 180));
        if (view === 'back') this.controls.rotateLeft(180 * (Math.PI / 180));
        if (view === 'top') {
          this.controls.maxPolarAngle = 0;
          this.controls.minPolarAngle = 0;
          this.controls.rotateLeft(Math.PI);
          this.controls.rotateUp(Math.PI / 2);
        }
      }
      if (Number.isFinite(opacity)) {
        this.mesh.material.originOpacity = this.mesh.material.opacity;
        this.mesh.material.opacity = opacity;
      }

      this.animate();
    },
    measurementToPositions(measurement) {
      const { line_points: linePoints, closed: closeLine } = measurement;
      if (!linePoints) return [];
      return this.linePointsToPositions(linePoints, closeLine);
    },
    linePointsToPositions(linePoints, closeLine = false) {
      const positions = [];
      for (let i = 0; i < linePoints.length; i += 1) {
        positions.push(linePoints[i][0], linePoints[i][1], linePoints[i][2]);
      }
      if (closeLine) {
        positions.push(linePoints[0][0], linePoints[0][1], linePoints[0][2]);
      }

      return positions;
    },
    addLine(positions, material, lineWidth = 1.2) {
      const geometry = new LineGeometry();
      geometry.setPositions(positions);
      const line = new Line2(geometry, material);
      this.currLines.push(line);
      this.scene.add(line);

      if (lineWidth > 1) this.increaseLineWidth(geometry, material, 1.2);
    },
    createReferenceLines(measurement) {
      const { line_points: linePoints, ref_points: refPoints } = measurement;

      if (!Array.isArray(linePoints) || linePoints.length < 2) return;
      if (!Array.isArray(refPoints) || refPoints.length !== 2) return;

      const firstPoint = linePoints[0];
      const lastPoint = linePoints[linePoints.length - 1];

      const refLine1 = [
        firstPoint[0],
        firstPoint[1],
        firstPoint[2],
        refPoints[0][0],
        refPoints[0][1],
        refPoints[0][2],
      ];
      const refLine2 = [
        lastPoint[0],
        lastPoint[1],
        lastPoint[2],
        refPoints[1][0],
        refPoints[1][1],
        refPoints[1][2],
      ];

      this.addLine(refLine1, this.matRefLine, 1);
      this.addLine(refLine2, this.matRefLine, 1);
    },
    increaseLineWidth(lineGeometry, lineMaterial, thickness) {
      for (let i = 0; i < 20; i += 1) {
        const routerLine = new Line2(lineGeometry, lineMaterial);
        routerLine.applyMatrix4(
          new THREE.Matrix4().makeTranslation(
            Math.cos(18 * i) * thickness,
            Math.sin(18 * i) * thickness,
            Math.cos(18 * i) * thickness
          )
        );
        this.currLines.push(routerLine);
        this.scene.add(routerLine);
      }
    },
    render() {
      if (!this.renderer) return;
      this.renderer.render(this.scene, this.camera);
      this.controls.update();
    },
    onWindowResize() {
      this.updateResolution();
    },
    updateResolution() {
      if (!this.container || !this.camera) return;
      this.camera.aspect = this.container.clientWidth / this.container.clientHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(this.container.clientWidth * 2, this.container.clientHeight * 2);
      this.renderer.domElement.style.width = '100%';
      this.renderer.domElement.style.height = '100%';
      this.matLine.resolution.set(this.container.clientWidth, this.container.clientHeight);
      this.matRefLine.resolution.set(
        this.container.clientWidth * 2,
        this.container.clientHeight * 2
      );
      this.render();
    },
    drawBreastTriangle(points) {
      const geometry = new THREE.BufferGeometry();
      const position = new Float32Array(points.flat());
      geometry.setAttribute('position', new THREE.BufferAttribute(position, 3));
      const mesh = new THREE.Mesh(geometry, this.matPlane);
      return mesh;
    },
    drawPlane(isSagittal = false) {
      const bbox = new THREE.Box3();
      bbox.setFromObject(this.mesh);
      const size = bbox.getSize(new THREE.Vector3());
      const center = bbox.getCenter(new THREE.Vector3());
      const geometry = new THREE.BoxGeometry(size.x, size.y, 5);
      const plane = new THREE.Mesh(geometry, this.matPlane);
      plane.position.set(center.x, center.y, center.z);
      if (isSagittal) {
        plane.rotation.set(0, Math.PI / 2, 0);
      }
      return plane;
    },
    drawVolume(points = []) {
      const geometry = new THREE.BufferGeometry();
      const position = new Float32Array(points.flat());
      geometry.setAttribute('position', new THREE.BufferAttribute(position, 3));
      geometry.computeBoundingBox();
      const material = new THREE.ShaderMaterial({
        side: THREE.DoubleSide,
        transparent: true,
        uniforms: {
          opacity: {
            value: 0.3,
          },
          color1: {
            value: new THREE.Color(0x2245ff).convertSRGBToLinear(),
          },
          color2: {
            value: new THREE.Color(0x4300d9).convertSRGBToLinear(),
          },
          bboxMin: {
            value: geometry.boundingBox.min,
          },
          bboxMax: {
            value: geometry.boundingBox.max,
          },
        },
        vertexShader: `
          uniform vec3 bboxMin;
          uniform vec3 bboxMax;
        
          varying vec2 vUv;

          void main() {
            vUv.y = (position.y - bboxMin.y) / (bboxMax.y - bboxMin.y);
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          uniform vec3 color1;
          uniform vec3 color2;
          uniform float opacity;
        
          varying vec2 vUv;
          
          void main() {
            gl_FragColor = vec4(mix(color1, color2, vUv.y), opacity);
          }
        `,
      });
      const mesh = new THREE.Mesh(geometry, material);
      return mesh;
    },
    drawPointCloud(points = [], color = 0x4300d9, size = 5, glow = false) {
      const geometry = new THREE.BufferGeometry();
      const position = new Float32Array(points.flat());
      geometry.setAttribute('position', new THREE.BufferAttribute(position, 3));
      geometry.setAttribute(
        'position',
        BufferGeometryUtils.mergeVertices(geometry, 1).attributes.position
      );
      const material = new THREE.PointsMaterial({
        color: new THREE.Color(color).convertSRGBToLinear(),
        size: glow ? size * 4 : size,
        transparent: true,
        map: glow ? this.pointTextureGlow : null,
        alphaMap: glow ? null : this.pointTexture,
        depthWrite: false,
        polygonOffset: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: -100,
      });
      const pointCloud = new THREE.Points(geometry, material);
      return pointCloud;
    },
  },
  computed: {
    isPointShader() {
      return this.shaderType === 'point';
    },
    isCustomShader() {
      return this.shaderType === 'custom';
    },
    isDefaultShader() {
      return this.shaderType === 'default';
    },
    actualPreset() {
      let preset = {};
      const defaultPreset = ShaderPresets.default;
      if (this.isDefaultShader) return defaultPreset;

      if (this.isCustomShader) {
        preset = this.customShader || {};
      } else {
        preset = ShaderPresets[this.shaderType] || {};
      }
      return { ...defaultPreset, ...preset };
    },
    actualMaterialColor() {
      return this.actualPreset.material.color;
    },
  },
  watch: {
    measurement(nv) {
      this.clearMeasurements();

      if (nv) {
        if (Array.isArray(nv)) {
          nv.forEach(m => {
            this.drawMeasurement(m);
          });
        } else {
          this.drawMeasurement(nv);
        }
      }
    },
    objUrl(nv) {
      if (nv) {
        if (!this.modelLoaded) this.renderModel(nv);
        else this.loadModel(nv);
      }
    },
    showFeet(nv) {
      this.showHideFoot(...nv);
    },
    modelLoaded(nv) {
      if (nv) {
        setTimeout(() => {
          this.$emit('loaded');
        }, 50);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.threeViewer {
  background-color: transparent;
  &.bg-white {
    background: white;
  }
}
</style>
